import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

import cloneDeep from 'lodash-es/cloneDeep';
import isBoolean from 'lodash-es/isBoolean';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { CompactType, DisplayGrid, GridsterConfig, GridsterItem, GridType } from 'angular-gridster2';
import { Account } from 'app/core/models/account.types';
import { PropertiesService } from '../../properties.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Property, ProjectTemplate } from 'app/modules/projects/projects.types';
import { ProjectTemplateSaveComponent } from 'app/modules/projects/modals/project-template-save/project-template-save.component';
import { combineLatest, EMPTY, filter, noop, switchMap, tap } from 'rxjs';
import { guid } from 'app/shared/Utils/common.utils';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag, NewPermission } from '@core/enums';
import { UserState } from '@state/user/state';
import { PanelDef } from '@shared/components/panels/panels.types';
import { ProjectTemplateService } from 'app/modules/projects/project-template.service';

@Component({
  selector: 'property-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDashboardComponent extends OnDestroyMixin implements OnInit {
  public data: Data;
  public property: Property;
  public account: Account;
  public panels: any[] = [];
  private oldPanels: any;
  private oldLayout: any;
  private _definition: any;
  public propertyTemplate: ProjectTemplate<any>;
  public propertyTemplateMemento: ProjectTemplate<any>;
  public editMode: boolean = false;

  // Gridster
  public gridOptions: GridsterConfig;
  public dashboardLayout: Array<GridsterItem> = [];
  public panelLookup = {};
  public currentTabId: any;
  public newLayout: boolean = false;
  readonly = false;

  private propertyRoute: string;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly matDialog: MatDialog,
    private readonly projectsService: ProjectsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly projectTemplateService: ProjectTemplateService,
    private readonly propertiesService: PropertiesService,
    private readonly store: Store,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  ngOnInit(): void {
    this.propertiesService.property$
      .pipe(
        untilComponentDestroyed(this),
        filter((property: Property) => !!property),
        tap((property) => {
          this.property = property;
          this.setProjectRoute();
        }),
        switchMap((property) => {
          if (property?.template?.id) {
            return this.projectsService.getProjectTemplateById(property.template?.id);
          }
          return EMPTY;
        }),
      )
      .subscribe(() => {
        if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
          this.readonly = !(
            this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_EDIT)) &&
            this.property?.permissions?.is_editable
          );
        }
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([this.activatedRoute.data, this.activatedRoute.params, this.projectsService.projectTemplate$])
      .pipe(untilComponentDestroyed(this))
      .subscribe(([data, params, propertyTemplate]) => {
        this.data = data;
        if (propertyTemplate) {
          this.propertyTemplate = propertyTemplate;
          // Check if the tab exists in the project template before assigning currentTabId
          if (params?.tab_id && this.tabExistsInTemplate(this.propertyTemplate, params.tab_id)) {
            this.currentTabId = params.tab_id;
          } else {
            this.currentTabId = null;
          }
          this.handleInitialTemplateSetup(this.propertyTemplate);
        }
      });

    this.gridOptions = {
      gridType: GridType.VerticalFixed,
      compactType: CompactType.None,
      displayGrid: DisplayGrid.OnDragAndResize,
      pushItems: true,
      margin: 0,
      minCols: 3,
      maxCols: 100,
      minRows: 1,
      maxRows: 100,
      maxItemCols: 100,
      minItemCols: 1,
      maxItemRows: 100,
      minItemRows: 1,
      maxItemArea: 2500,
      minItemArea: 1,
      defaultItemCols: 1,
      defaultItemRows: 1,
      fixedColWidth: 105,
      fixedRowHeight: 105,
      outerMargin: false,
      draggable: {
        enabled: true,
      },
      resizable: {
        enabled: true,
      },
    };

    this.propertiesService.changePanelState$.pipe(untilComponentDestroyed(this)).subscribe((state) => {
      const saveChange = this.editMode !== state;
      this.editing(!!state);

      if (this.propertyTemplate) {
        if (isBoolean(state) && !state) {
          if (this.currentTabId) this.setTemplate(null);
          else this.setOldTemplate(null);
          if (saveChange) this.showTemplateSaveDialog(this.propertyTemplate);
        } else {
          if (this.oldPanels?.length) {
            this.panels = this.oldPanels?.length ? cloneDeep(this.oldPanels) : [];
            this.dashboardLayout = this.oldLayout?.length ? cloneDeep(this.oldLayout) : [];
            if (this.currentTabId) {
              this._definition.panels = this.panels;
              this._definition.layout = this.dashboardLayout;
            } else {
              this._definition.dashboard.panels = this.panels;
            }
            this.changeDetectorRef.markForCheck();
          }
        }
      }

      this.changeDetectorRef.markForCheck();
    });

    this.propertiesService.panelDefinition$.pipe(untilComponentDestroyed(this)).subscribe((panelDef) => {
      if (panelDef) {
        if (!('id' in panelDef)) {
          panelDef['id'] = guid();

          if (this.currentTabId) {
            this.panels.push(panelDef);
            this.setTemplate(null);
          } else {
            this.panels[0].unshift(panelDef);
            this.setOldTemplate(null);
          }

          this.addItem(panelDef);
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private handleInitialTemplateSetup(propertyTemplate: ProjectTemplate<any>): void {
    this.propertyTemplateMemento = cloneDeep(propertyTemplate);
    this.panels = this.propertyTemplate.definition?.panels || [];

    if (this.currentTabId) {
      this.newLayout = true;
      if (this.propertyTemplate.definition && this.propertyTemplate.definition.dashboard) {
        this.setTemplate(propertyTemplate);
      } else {
        console.error('Project template definition or dashboard is not set', { propertyTemplate });
      }
    } else {
      this.newLayout = false;
      this.setOldTemplate(propertyTemplate);
    }
    this.changeDetectorRef.markForCheck();
  }

  private tabExistsInTemplate(propertyTemplate: ProjectTemplate<any>, tabId: string): boolean {
    return propertyTemplate?.definition?.dashboard?.tabs?.some((tab) => tab?.id === tabId) ?? false;
  }

  setProjectRoute(): void {
    this.propertyRoute = '/properties/' + this.property.id;
  }

  setTemplate(template: ProjectTemplate<any>): void {
    if (template) this.propertyTemplate = template;

    this.setDefinition();

    if (!this.editMode && this._definition && this._definition.panels) {
      this.panels = this._definition.panels;
    } else {
      this._definition.panels = this.panels;
    }

    this.setLayout();

    if (template) {
      this.oldPanels = cloneDeep(this.panels);
      this.oldLayout = cloneDeep(this.dashboardLayout);
    }

    this.cleanPanels();
    this.changeDetectorRef.markForCheck();
  }

  setOldTemplate(template: ProjectTemplate<any>): void {
    if (template) this.propertyTemplate = template;

    if (JSON.stringify(this.panels) != JSON.stringify(this.propertyTemplate.definition.dashboard.panels)) {
      if (!this._definition || this.propertyTemplate.id != this._definition.id) {
        this._definition = this.propertyTemplate.definition;
        this.panels = this._definition.dashboard.panels;
      } else {
        this._definition.dashboard.panels = this.panels;
        this.propertyTemplate.definition = this._definition;
      }
    } else {
      if (!this.panels) {
        this._definition = this.propertyTemplate.definition;
        this.panels = this._definition.dashboard.panels;
      }
    }
    if (template) this.oldPanels = cloneDeep(this.panels);
    this.changeDetectorRef.markForCheck();
  }

  setDefinition(): void {
    const { dashboard } = this.propertyTemplate.definition;

    // If there's no current tab ID or no tabs in the dashboard, use the entire dashboard as the definition.
    if (!this.currentTabId || !dashboard.tabs) {
      this._definition = dashboard;
      return;
    }

    const foundTab = dashboard.tabs.find((tab) => tab.id === this.currentTabId);

    if (foundTab) {
      this._definition = foundTab;
      return;
    }
  }

  setLayout(): void {
    if (this.currentTabId) {
      this.dashboardLayout = this._definition.layout;
      this.mapPanels();
    } else this.convertPanelsToDashboardLayout(this.panels);
  }

  mapPanels(): void {
    if (!this.currentTabId) {
      if (this._definition.dashboard.panels) {
        for (const col in this._definition.dashboard.panels) {
          for (const row in this._definition.dashboard.panels[col]) {
            const panel = this.panels[col][row];
            this.panelLookup[panel.id] = panel;
          }
        }
      }
    } else {
      this.panels.forEach((panel) => {
        this.panelLookup[panel.id] = panel;
      });
    }
  }

  cleanPanels(): any {
    if (!this.currentTabId) {
      for (const col in this.panels) {
        for (const row in this.panels[col]) {
          const panelId = this.panels[col][row].id;
          const layoutPanel = this.dashboardLayout.find((layout) => layout.id === panelId);
          if (!layoutPanel) {
            const panelIndex = this.panels[col].indexOf(this.panels[col][row]);
            this.panels[col].splice(panelIndex, 1);
          }
        }
      }
    }
  }

  convertPanelsToDashboardLayout(panels) {
    for (const col in panels) {
      for (const row in panels[col]) {
        const panel = this.panels[col][row];
        this.panelLookup[panel.id] = panel;
        this.dashboardLayout.push({
          id: panel.id,
          x: parseFloat(col),
          y: parseFloat(row),
          cols: 1,
          rows: 3,
        });
      }
    }

    this._definition.layout = this.dashboardLayout;
  }

  editing(state: boolean): void {
    this.editMode = state;
    this.gridOptions.resizable.enabled = state;
    this.gridOptions.draggable.enabled = state;

    if (this.editMode) {
      this.gridOptions.displayGrid = DisplayGrid.Always;
    } else {
      this.gridOptions.displayGrid = DisplayGrid.OnDragAndResize;
    }

    this.changedOptions();
  }

  changedOptions(): void {
    if (this.gridOptions.api && this.gridOptions.api.optionsChanged) {
      this.gridOptions.api.optionsChanged();
    }
  }

  removeItem($event: MouseEvent | TouchEvent, item): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.dashboardLayout?.splice(this.dashboardLayout.indexOf(item), 1);
  }

  addItem(panelDef): void {
    this.panelLookup[panelDef.id] = panelDef;
    const [cols, rows] = panelDef.type === 'chart' ? [2, 3] : [1, 3];
    this.dashboardLayout?.push({ x: 0, y: 0, cols, rows, id: panelDef.id });
  }

  lookupPanelDef(id) {
    return this.panelLookup[id];
  }

  onEditPanelDef(panelDef: PanelDef<any>): void {
    this.editPanelDef(panelDef);
  }

  onSilentPanelDefUpdated(panelDef: PanelDef<any>): void {
    this.editPanelDef(panelDef, true);
  }

  onDeletePanelDef(panelDef): void {
    for (const col in this.panels) {
      for (const row in this.panels[col]) {
        const panel = this.panels[col][row];
        if (panel.id == panelDef.id) {
          this.panels[col].splice(row, 1);
        }
      }
    }
    this._definition.dashboard.panels = this.panels;
    const propertyTemplate = this.propertyTemplate;
    propertyTemplate.definition = this._definition;
  }

  showTemplateSaveDialog(propertyTemplate: ProjectTemplate<any>): void {
    const dialogRef = this.matDialog.open(ProjectTemplateSaveComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { project_template: propertyTemplate, project_id: this.property.id, related_to_type: 'Property' },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (!result) {
          this.panels = this.oldPanels?.length ? cloneDeep(this.oldPanels) : [];
          this.dashboardLayout = this.oldLayout?.length ? cloneDeep(this.oldLayout) : [];
          this.propertyTemplate = cloneDeep(this.propertyTemplateMemento);
          this.projectsService._projectTemplate.next(this.propertyTemplate);
          this.changeDetectorRef.detectChanges();
        } else {
          this.oldPanels = cloneDeep(this.panels);
          this.oldLayout = cloneDeep(this.dashboardLayout);
          this.propertiesService
            .getPropertyById(this.property.id.toString(), true)
            .pipe(untilComponentDestroyed(this))
            .subscribe(() => this.changeDetectorRef.markForCheck());
        }
      });
  }

  dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    this.changeDetectorRef.markForCheck();
  }

  @ViewChildren('panels') panelElements: QueryList<any>;

  onUpdate(data: any): void {
    this.propertiesService
      .saveProperty({ ...data, id: this.property.id }, this.property)
      .pipe(
        untilComponentDestroyed(this),
        tap(() => {
          this.panelElements
            .toArray()
            .filter((x) => x.panelDef.type === 'matrix')
            .forEach((matrix) => matrix.instance.completeSaving());
        }),
      )
      .subscribe(noop);
  }

  private editPanelDef(panelDef: PanelDef<any>, silent = false): void {
    if (this.currentTabId) {
      const panelIndex = this._definition.panels.findIndex(({ id }) => id === panelDef.id);
      if (panelIndex > -1) {
        this._definition.panels[panelIndex] = { ...panelDef };
      }
      this.propertyTemplate?.definition?.dashboard?.tabs?.forEach((tab: any) => {
        if (tab.id === this.currentTabId) {
          tab = this._definition;
        }
      });
    } else {
      for (const col in this.panels) {
        for (const row in this.panels[col]) {
          const panel = this.panels[col][row];
          if (panel.id == panelDef.id) {
            this.panels[col][row] = panelDef;
          }
        }
      }
      this._definition.dashboard.panels = this.panels;
      this.propertyTemplate.definition = this._definition;
    }

    if (!silent) {
      this.showTemplateSaveDialog(this.propertyTemplate);
    } else {
      this.projectTemplateService
        .update({ ...this.propertyTemplate, account_id: Number(this.account.id) })
        .pipe(untilComponentDestroyed(this))
        .subscribe(noop);
    }
  }
}
